import {
  AnalyticsEventName,
  EXTENSION_ID,
  FeatureFlags,
  MessageTypes,
} from '@dupe/common/constants'
import axios from 'axios'
import {
  type OptionsType,
  deleteCookie,
  getCookie,
  setCookie,
} from 'cookies-next'
import { User, onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'next/compat/router'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isFirefox } from 'react-device-detect'
import { useAuthUser } from '~/src/hooks'
import useAnalytics from '~/src/hooks/useAnalytics'
import { useAuthWebToken } from '~/src/hooks/useAuthWebToken'
import useFetchUser from '~/src/hooks/useFetchUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import { useMaybeRegister } from '~/src/hooks/useMaybeRegister'
import usePostMessageListener from '~/src/hooks/usePostMessageListener'
import { DUPE_AUTH_USER_ID_KEY, MAX_AGE_OF_COOKIE } from '~/src/lib/constants'
import { auth } from '~/src/lib/firebase/firebaseAuthInstance'
import getUserToken from '~/src/lib/getUserToken'
import { getStickyFlag } from '~/src/lib/stickyFeatureFlag'
import logger from '~/src/lib/logger'
import { FASHION_DEPARTMENT_DEFAULT } from '~/src/components/onboarding/styleOptions'
import { useDupeInputStore } from '~/src/stores/dupeInputStore'
import {
  shouldDropAbandonedConsent,
  usePendingMarketingConsentStore,
} from '~/src/stores/pendingMarketingConsentStore'
import { useOnboardingStore } from '~/src/stores/useOnboardingStore'
import { UpdateUserPayload } from '~/src/types'
import { stringToHash } from '~/src/lib/urlHelpers'
import { trackGoogleAdsAccountCreation } from '~/src/lib/analytics/googleAdsConversions'
import { trackMetaAccountCreation } from '~/src/lib/analytics/metaConversions'
import { recallFirstSearch } from '~/src/lib/analytics/firstSearchMemory'
import { personNameProps } from '@dupe/common/personName'
import { trackOpenAiAccountCreation } from '~/src/lib/analytics/openAiConversions'
import { idTokenForSend } from '~/src/lib/firebase/freshIdToken'

interface UserAuthData {
  uid: string
  accessToken: string
  tokenResult: any
}

interface OpenModalProps {
  callback?: (props?: {
    authUser: User | null | undefined
    userData: UpdateUserPayload | null | undefined
  }) => void
  onClose?: () => void
  allowLoggedIn?: boolean
  source?: string
  modalTitle?: React.ReactNode
  modalSubtitle?: React.ReactNode
  /**
   * Chrome that must SURVIVE a title swap (DUP-2460).
   *
   * `modalTitle` is an arbitrary node, and the results wall used it to carry
   * its icon, headline, subtitle AND result thumbnails as one blob. That made
   * "swap the title" mean "replace all four": when the phone email ask put its
   * own heading in that slot, the icon and thumbnails vanished with it and the
   * modal lost ~200px mid-morph. Anything a surface wants kept while the title
   * changes belongs here instead — the title slot is for words.
   */
  modalIcon?: React.ReactNode
  /** Rendered under the subtitle, at full opacity (the subtitle slot is
   *  dimmed). The results wall's "here's what you're unlocking" thumbnails. */
  modalFooter?: React.ReactNode
  modalAsset?: string
  skipPhoneLink?: boolean
  disableClose?: boolean
  fullScreen?: boolean
  blurOverlay?: boolean
  skipOnboarding?: boolean
}

interface UpdateUserDetailsProps {
  subscription: {
    plan: string
    billingPeriod?: string
    paymentStatus?: string
    liveMode?: boolean
  }
  stripeCustomerId?: string
  stripeSubscriptionId?: string
}

/**
 * Auth Callback Flow:
 *
 * There are three main scenarios for when the auth callback is executed:
 *
 * 1. User authenticates but closes modal before completing onboarding
 *    - Callback is executed when modal is closed
 *    - This allows the app to proceed with authenticated state even if onboarding is incomplete
 *
 * 2. User completes both auth and onboarding
 *    - Callback is executed via handleAuthSuccess
 *    - This is the "happy path" where all user data is available
 *
 * 3. User starts auth but cancels before completing
 *    - No callback is executed
 *    - Processing state is reset
 *
 * The callback receives both authUser and userData, allowing the app to:
 * - Know if the user is authenticated (authUser)
 * - Access additional user data if onboarding was completed (userData)
 */

interface AuthContextType {
  isOpen: boolean
  openAuthModal: (props?: OpenModalProps) => void
  closeAuthModal: () => void
  handleAuthSuccess: (close?: () => void) => void
  authSource: string | null
  modalTitle?: React.ReactNode
  modalSubtitle?: React.ReactNode
  modalIcon?: React.ReactNode
  modalFooter?: React.ReactNode
  modalAsset?: string | null
  userData: UpdateUserPayload | null | undefined
  loadingUserData: boolean
  refetchUserData: () => Promise<{ user: UpdateUserPayload } | undefined>
  updateUserDetails: (
    props: UpdateUserDetailsProps
  ) => Promise<{ status: boolean; userData: UpdateUserPayload }>
  extensionInstalled:
    | {
        installed: boolean
        version: string
      }
    | undefined
  skipPhoneLink?: boolean
  disableClose?: boolean
  fullScreen?: boolean
  blurOverlay?: boolean
  skipOnboarding?: boolean
  // True only while AuthContent is actually rendering the unified onboarding
  // flow (`PostSignupOnboarding`) — for BOTH the results wall and the
  // standalone signup, and NOT for the already-onboarded skip. AuthContent owns
  // the truth (it's the only place that knows the current step + skip state) and
  // publishes it here so AuthModal can size the shell as a constrained centered
  // modal once onboarding shows, regardless of how the preceding step was sized.
  onboardingActive: boolean
  setOnboardingActive: (active: boolean) => void
  // True only while the onboarding step actually on screen needs the WIDE shell
  // — i.e. the control arm's two-column `ResultsOnboarding`. The arm-invariant
  // HDYHAU survey (screen 1), the single-column brand arm, and the loading
  // spinner all use the narrow onboarding width, so this stays false for them.
  // AuthContent publishes it from the rendered phase (not a guessed arm) so the
  // shell only ever resizes when the content genuinely changes — never an
  // open-wide-then-shrink flicker while the experiment flag resolves.
  // True only while an onboarding step should offer the modal close (X) as its
  // exit — i.e. the `onboarding_first` placement arm (DUP-1903), where the
  // prominent skip on the opt-in step is removed and the X becomes the
  // least-used exit. Safe because onboarding is post-auth (the sign-up gate is
  // already satisfied); stays false for control, so the locked wall's usual
  // no-exit behaviour is unchanged. AuthContent publishes it from the rendered
  // arm.
  onboardingCloseable: boolean
  setOnboardingCloseable: (closeable: boolean) => void
}

interface AuthProviderProps {
  children: React.ReactNode
}

const defaultAuthContext: AuthContextType = {
  isOpen: false,
  openAuthModal: () => {},
  closeAuthModal: () => {},
  handleAuthSuccess: () => {},
  authSource: null,
  userData: null,
  loadingUserData: false,
  refetchUserData: async () => undefined,
  updateUserDetails: async () => ({
    status: false,
    userData: {} as UpdateUserPayload,
  }),
  extensionInstalled: undefined,
  skipPhoneLink: false,
  disableClose: false,
  fullScreen: true,
  blurOverlay: false,
  skipOnboarding: false,
  onboardingActive: false,
  setOnboardingActive: () => {},
  onboardingCloseable: false,
  setOnboardingCloseable: () => {},
}

const AuthContext = createContext<AuthContextType>(defaultAuthContext)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const analytics = useAnalytics()
  const {
    authUser,
    loading: loadingAuthUser,
    authUserNotAnonymous,
  } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  // next/compat/router: the pages NextRouter when mounted under pages/, null
  // under app/ (where URL cleanup falls back to history.replaceState below).
  const router = useRouter()

  // Initialize Auth Web Token
  const { wasAuthWebTokenActive } = useAuthWebToken()

  const [isOpen, setIsOpen] = useState(false)
  const [extensionInstalled, setExtensionInstalled] = useState<
    | {
        installed: boolean
        version: string
      }
    | undefined
  >()
  const [onAuthCallback, setOnAuthCallback] = useState<
    | ((props: {
        authUser: User | null | undefined
        userData: UpdateUserPayload | null | undefined
      }) => void)
    | null
  >(null)
  const [onCloseAuthModal, setOnCloseAuthModal] = useState<(() => void) | null>(
    null
  )
  const [authSource, setAuthSource] = useState<string | null>(null)
  const [modalTitle, setModalTitle] = useState<React.ReactNode | null>(null)
  const [modalSubtitle, setModalSubtitle] = useState<React.ReactNode | null>(
    null
  )
  const [modalIcon, setModalIcon] = useState<React.ReactNode | null>(null)
  const [modalFooter, setModalFooter] = useState<React.ReactNode | null>(null)
  const [modalAsset, setModalAsset] = useState<string | null>(null)
  const [skipPhoneLink, setSkipPhoneLink] = useState(false)
  const [disableClose, setDisableClose] = useState(false)
  const [fullScreen, setFullScreen] = useState(defaultAuthContext.fullScreen)
  const [blurOverlay, setBlurOverlay] = useState(defaultAuthContext.blurOverlay)
  const [skipOnboarding, setSkipOnboarding] = useState(false)
  // Published by AuthContent (see `onboardingActive` in the context type). The
  // useState setter has a stable identity, so it's safe to expose directly as a
  // dependency for AuthContent's publishing effect.
  const [onboardingActive, setOnboardingActive] = useState(false)
  const [onboardingCloseable, setOnboardingCloseable] = useState(false)

  const {
    data: userDataResp,
    isLoading: loadingUserData,
    refetch: refetchUser,
  } = useFetchUser()

  const userData = userDataResp?.user

  //  get posthog distinct id
  const posthogId = analytics.getPostHogDistinctId()

  // Dupe Input Store
  const resetDupeInputStore = useDupeInputStore((state) => state.reset)

  // Onboarding Store
  const resetOnboarding = useOnboardingStore((state) => state.resetOnboarding)
  const setOnboardingStepData = useOnboardingStore((state) => state.setStepData)

  const { mutateAsync: maybeRegisterUser } = useMaybeRegister()

  const updateUserDetails = async (props: UpdateUserDetailsProps) => {
    const { subscription, stripeCustomerId, stripeSubscriptionId } = props
    const valuesToStore = {
      metadata: {
        subscription,
        stripeCustomerId,
        stripeSubscriptionId,
      },
    }

    try {
      const token = await idTokenForSend(authUser)
      const res = await axios<{ metadata?: Record<string, any> }>(
        '/api/secure/update-user',
        {
          method: 'POST',
          data: valuesToStore,
          headers: {
            'x-authorization': token,
          },
        }
      )

      if (res?.status !== 200) {
        return {
          status: false,
          userData: {} as UpdateUserPayload,
          error: 'Error updating user details',
        }
      }

      const userData = await refetchUserData()
      return {
        status: true,
        userData: userData?.user || ({} as UpdateUserPayload),
      }
    } catch (error) {
      logger.error('[updateUserDetails]', { error })
      return {
        status: false,
        userData: {} as UpdateUserPayload,
        error: 'Error updating user details',
      }
    }
  }

  const refetchUserData = useCallback(async () => {
    const result = await refetchUser()
    return result.data
  }, [refetchUser])

  /**
   * Opens the auth modal and sets up the callback
   * The callback will be executed either:
   * - When the user completes auth + onboarding
   * - When the user authenticates but closes before completing onboarding
   */
  const openAuthModal = useCallback(
    (props?: OpenModalProps) => {
      // Using authUserNotAnonymous instead of authUser to also ensure that the user is not anonymous
      // and avoid closing the modal if the user is technically authenticated but is anonymous
      if (authUserNotAnonymous && !props?.allowLoggedIn) {
        logger.debug('[user already authenticated] exit')
        setIsOpen(false)
        return
      }

      // Reset on every open rather than on close: registration can finish
      // after the modal has closed (`skipOnboarding`), and it reads this then.
      // Keeping a stale source across opens credited sign-ups to the wrong
      // entry point.
      setAuthSource(props?.source ?? null)

      if (!!props?.modalTitle) {
        setModalTitle(props.modalTitle)
      }

      if (!!props?.modalSubtitle) {
        setModalSubtitle(props.modalSubtitle)
      }

      if (!!props?.modalIcon) {
        setModalIcon(props.modalIcon)
      }

      if (!!props?.modalFooter) {
        setModalFooter(props.modalFooter)
      }

      if (!!props?.modalAsset) {
        setModalAsset(props.modalAsset)
      }

      if (!!props?.blurOverlay) {
        setBlurOverlay(!!props?.blurOverlay)
      }

      setIsOpen(true)
      setOnAuthCallback(() => props?.callback || null)
      setOnCloseAuthModal(() => props?.onClose || null)

      setSkipPhoneLink(!!props?.skipPhoneLink)
      setDisableClose(!!props?.disableClose)
      setSkipOnboarding(!!props?.skipOnboarding)

      if (props?.fullScreen !== undefined) {
        setFullScreen(!!props?.fullScreen)
      }

      if (props?.blurOverlay !== undefined) {
        setBlurOverlay(!!props?.blurOverlay)
      }

      analytics.track(
        AnalyticsEventName.AuthModalOpened,
        {
          source: props?.source,
          // Only forward title/subtitle when they're plain strings — callers may
          // pass a ReactNode for display (e.g. the results sign-up wall), which
          // isn't serializable and would reach PostHog/Plausible/FB as garbage.
          ...(typeof props?.modalTitle === 'string' && {
            modalTitle: props.modalTitle,
          }),
          ...(typeof props?.modalSubtitle === 'string' && {
            modalSubtitle: props.modalSubtitle,
          }),
          ...(props?.disableClose && { disableClose: props.disableClose }),
          ...(props?.allowLoggedIn && { allowLoggedIn: props.allowLoggedIn }),
        },
        {
          sendToPlausible: true,
          sendToFb: true,
        }
      )
    },
    [analytics, authUserNotAnonymous]
  )

  /**
   * Closes the auth modal
   * If the user is authenticated but hasn't completed onboarding,
   * we still want to execute their callback to maintain the authenticated state
   */
  const closeAuthModal = () => {
    logger.debug('[close triggered] closeAuthModal')
    setIsOpen(false)
    setOnAuthCallback(null)
    setOnCloseAuthModal(null)
    // Clear immediately (not in the delayed reset) so the shell can't briefly
    // keep the wide onboarding size on the next open before AuthContent remounts.
    setOnboardingActive(false)
    setOnboardingCloseable(false)

    // DUP-1903: drop an ABANDONED pre-auth marketing-consent capture. If the
    // shopper wrote a choice on the `account_creation` sign-up screen and then
    // dismissed WITHOUT AUTHENTICATING, the captured `consent` is stale — left
    // in this module-level store it could be picked up by a later, unrelated
    // registration (e.g. a subsequent sign-in on a surface where the widget is
    // suppressed) and silently flushed.
    //
    // Requires `!authUser`, not just an unset `registeredAt`: the stamp arrives
    // with `maybeRegisterUser`'s async response, and on `skipOnboarding` entries
    // the modal closes the moment auth succeeds — before it lands. Clearing on
    // `registeredAt` alone therefore dropped genuine consent from a real
    // registration. See `shouldDropAbandonedConsent`.
    if (
      shouldDropAbandonedConsent({
        authenticated: !!authUser,
        registeredAt: usePendingMarketingConsentStore.getState().registeredAt,
      })
    ) {
      usePendingMarketingConsentStore.getState().clear()
    }

    // reset modal state
    // slight delay to avoid ui flicker
    setTimeout(() => {
      setSkipPhoneLink(false)
      setDisableClose(false)
      setFullScreen(true)
      setBlurOverlay(false)
      setSkipOnboarding(false)
      setModalTitle(null)
      setModalSubtitle(null)
      setModalIcon(null)
      setModalFooter(null)
      setModalAsset(null)
    }, 300)

    // Clean up URL parameters
    if (router) {
      // Pages router: shallow replace strips the params without re-running
      // data fetching.
      if (router.query.onboarding || router.query.step) {
        const newQuery = { ...router.query }
        delete newQuery.onboarding
        delete newQuery.step

        router.replace(
          {
            pathname:
              router.pathname === '/[hash]' ? '/chats/[hash]' : router.pathname,
            query: newQuery,
          },
          undefined,
          { shallow: true }
        )
      }
    } else {
      // App Router has no shallow router.replace — strip the params via
      // history.replaceState (the documented App Router shallow-routing
      // equivalent).
      const url = new URL(window.location.href)
      if (url.searchParams.has('onboarding') || url.searchParams.has('step')) {
        url.searchParams.delete('onboarding')
        url.searchParams.delete('step')
        window.history.replaceState(null, '', url.toString())
      }
    }

    // Only execute onClose callback if Auth Web Token was not active
    if (onCloseAuthModal && !wasAuthWebTokenActive) {
      onCloseAuthModal()
    }
  }

  /**
   * Handles successful authentication and executes the callback
   * This is called in two scenarios:
   * 1. User completes the full auth + onboarding flow
   * 2. User authenticates but closes modal before completing onboarding
   */
  const handleAuthSuccess = (close?: () => void) => {
    // Only execute auth callback if Auth Web Token was not active
    if (onAuthCallback && !wasAuthWebTokenActive) {
      onAuthCallback({ authUser, userData })
      setOnAuthCallback(null)
    }

    close && close()
  }

  const cleanGACookie = (ga: string): string => {
    try {
      return ga.split('.')[2] + '.' + ga.split('.')[3]
    } catch (_e) {
      return ga
    }
  }

  const createUserAuthData = async (
    authUser: User | null | undefined
  ): Promise<UserAuthData | undefined> => {
    if (!authUser) return undefined

    const tokenResult = await getUserToken(authUser)

    return {
      uid: authUser.uid,
      accessToken: tokenResult?.token as string,
      tokenResult,
    }
  }

  const postMessageListener = useCallback(
    async (messageType: string, data: Record<string, any>) => {
      setExtensionInstalled(
        (prev) =>
          prev || {
            installed: true,
            ...(data?.version && {
              version: data?.version,
            }),
          }
      )

      switch (messageType) {
        case MessageTypes.AUTH_SYNC: {
          // If we have auth cookie await auth user to be ready
          const authCookie = getCookie(DUPE_AUTH_USER_ID_KEY)
          if (authCookie) {
            await new Promise((resolve) => {
              const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (user) {
                  resolve(true)
                  unsubscribe()
                }
              })
            })
          } else {
            logger.debug('No auth cookie found')
          }

          const gaClientId = cleanGACookie(getCookie('_ga') as string)
          // Continue with auth sync
          const user = auth?.currentUser
          let authData: UserAuthData | undefined
          if (user) {
            authData = await createUserAuthData(user)
          } else {
            logger.debug('No user found to sync')
          }

          // Handle Chrome only to begin with - Later add support for Firefox as does not support `externally_connactable`
          // For Chrome (and Safari) we can message directly to the service worker with sendMessage
          // @ts-ignore
          if (isFirefox) {
            // Support for Firefox
            // send post message
            window.postMessage(
              {
                type: MessageTypes.AUTH_SYNC,
                extensionId: EXTENSION_ID,
                browser: 'firefox',
                userId: user?.uid,
                authData,
                tempUserId,
                gaClientId,
                posthogId,
              },
              '*'
            )
          } else {
            // @ts-ignore
            if (typeof chrome !== 'undefined') {
              // Wrap in try/catch to solve for browser like Arc which are chromium based but not will full chrome feature set.
              try {
                // @ts-ignore
                chrome.runtime.sendMessage(EXTENSION_ID, {
                  type: MessageTypes.AUTH_SYNC,
                  userId: user?.uid,
                  authData,
                  tempUserId,
                  gaClientId,
                  posthogId,
                })
              } catch (e) {
                //noop - likely a browser that is not chrome based such as Arc
                logger.debug('Arc browser error sending message', { error: e })
              }
            } else {
              // Fallback for other browser here
              logger.debug('Fallback for other browser here')
            }
          }
          break
        }
        default:
          break
      }
    },
    [posthogId, tempUserId]
  )

  usePostMessageListener('AuthProvider', postMessageListener)

  // Latest analytics, read through a ref so the auth-state effect below never
  // lists `analytics.track` as a dependency. `track`'s identity changes with
  // authUser/tempUserId/route (see useAnalytics), and authUser flips exactly at
  // signup — depending on it would tear down and re-subscribe onAuthStateChanged
  // mid-registration. onAuthStateChanged replays the current user on subscribe,
  // so that would re-enter the maybe-register block and could double-fire the
  // registration event + Podscribe signup before userData settles.
  const analyticsRef = useRef(analytics)
  useEffect(() => {
    analyticsRef.current = analytics
  }, [analytics])

  // listen for auth state changes
  useEffect(() => {
    if (loadingAuthUser) return
    if (loadingUserData) return

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user?.uid) {
        // Update auth cookie
        const cookieOptions: OptionsType = {
          expires: new Date(Date.now() + 1000 * MAX_AGE_OF_COOKIE),
          maxAge: MAX_AGE_OF_COOKIE,
          path: '/',
        }

        if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
          cookieOptions.domain = '.dupe.com'
        }

        setCookie(DUPE_AUTH_USER_ID_KEY, user.uid, cookieOptions)

        // Extract promo code from auth source, if it is present and starts with 'vanity-url:'
        let promoCode: string | undefined
        if (authSource?.startsWith('vanity-url:')) {
          promoCode = authSource.replace('vanity-url:', '').trim()
        }

        // Maybe register user
        if (!userData?.id) {
          // Hand the register call the user Firebase just gave us, rather than
          // letting it read `useAuthUser`. We are INSIDE `onAuthStateChanged`,
          // so React has not re-rendered and that hook still reads `null` —
          // which `fetcher` turns into a request with no `x-authorization`
          // header rather than an error, so the API answers 401 and no account
          // is created. Measured on a phone sign-up (2026-09-21, preview): the
          // first call always went out unauthenticated.
          const registerResult = await maybeRegisterUser({
            promoCode,
            authUser: user,
          })
          const registeredUser = registerResult?.user

          // DUP-1903: a returning sign-in (`existing === true`) never stamps
          // `registeredAt`, so a consent captured on the way in can never be
          // flushed — and until now nothing cleared it either. Left in the
          // module-level store it could be stamped onto a LATER, unrelated
          // registration in the same tab, subscribing that person on this one's
          // answer and recording their jurisdiction and consent basis. Drop it
          // the moment we know it will never apply.
          //
          // ⚠️ `registeredAt` guards a RE-ENTRY, and it started mattering the
          // moment the call above began succeeding. This effect re-subscribes
          // whenever `userData` settles, and `onAuthStateChanged` replays the
          // current user on subscribe (see the `analyticsRef` note above), so
          // the block can run twice for one sign-up. The second run is a repeat
          // call for a user that now exists, so it answers `existing: true` —
          // and without this guard it would clear a capture that the FIRST run
          // had already stamped as a genuine new registration, silently
          // cancelling the DUP-2460 email ask that is waiting to read it.
          //
          // Harmless before, because the first call 401'd and never stamped.
          if (
            registerResult?.existing === true &&
            usePendingMarketingConsentStore.getState().consent &&
            !usePendingMarketingConsentStore.getState().registeredAt
          ) {
            usePendingMarketingConsentStore.getState().clear()
          }

          // New account confirmed server-side (existing === false) — the single
          // registration moment, deduped by maybe-register (a repeat call for the
          // same user returns existing === true). Gate strictly on `=== false` so
          // a missing/failed result never counts as a registration.
          if (registerResult?.existing === false) {
            // The API has confirmed this is a newly created account (including
            // OAuth), rather than a returning login. Use the stable auth uid as
            // Google's transaction id and session-dedupe against auth replays.
            trackGoogleAdsAccountCreation(user.uid)
            // Guest identities (notably logged-out extension installs) also
            // create user rows. Meta should optimize for actual signups.
            if (!user.isAnonymous) {
              trackMetaAccountCreation(user.uid)
              // ChatGPT Ads' copy of the same signup (DUP-2570), on OpenAI's
              // `registration_completed` standard event. Inside the
              // `isAnonymous` guard with Meta rather than outside with Google:
              // guest identities (logged-out extension installs) create user
              // rows too, and the campaign this feeds bids on real accounts.
              // The uid is the event id, so a repeat is collapsed by OpenAI as
              // well as by our own mark. See `openAiConversions.ts`.
              trackOpenAiAccountCreation(user.uid)
            }

            // Clean, client-side registration event: the activation trigger for
            // the post-registration "How did you hear about us?" PostHog survey
            // (DUP-1563) and a precise new-user acquisition signal. Client-side so
            // posthog-js can render the survey off it (server events can't). The
            // event doesn't need the registered-user id, so it isn't gated on it —
            // only Podscribe is (it uses the id as device_id). Fired via
            // analyticsRef so this effect doesn't depend on `analytics.track`.
            // DUP-1903 arm, so a readout can split registrations by the
            // placement the shopper actually saw — without it you can only
            // compare against the configured traffic split and hope.
            //
            // ⛔ Read through `getStickyFlag`, NEVER `useOptinPlacement` here.
            // That hook would have to sit at this component's top level, and
            // `AuthProvider` wraps EVERY page — so it would emit a
            // `$feature_flag_called` exposure per page view. That event was
            // ~36% of all events before the allowlist, and this flag is ON the
            // allowlist, so it would both cost real money and count people into
            // experiment 434463 who never went near sign-up.
            //
            // `getStickyFlag` is a plain Map read: no subscription, no PostHog
            // call, no event. It returns the value the auth modal already froze
            // — which is also the arm the shopper genuinely SAW, rather than a
            // fresh read that posthog-js may have re-bucketed at `identify()`
            // (the documented hazard on this flag).
            //
            // Absent when someone registered without the modal ever resolving
            // the flag. Omitted rather than guessed: a wrong arm is worse than
            // a missing one.
            const seenArm = getStickyFlag(
              FeatureFlags.OnboardingMarketingOptinPlacement
            )?.variant
            const optinPlacement =
              seenArm === 'control' ||
              seenArm === 'onboarding_first' ||
              seenArm === 'account_creation'
                ? seenArm
                : undefined

            // Guests (no email or phone) get their own event so
            // `Registration Completed` always means a reachable account, on
            // web and app alike. Upgrading a guest signs in with a fresh uid,
            // which comes back through here as a real registration.
            if (user.isAnonymous) {
              analyticsRef.current.track(
                AnalyticsEventName.GuestAccountCreated,
                { source: authSource, method: 'anonymous', isGuest: true }
              )
            } else {
              analyticsRef.current.track(
                AnalyticsEventName.RegistrationCompleted,
                {
                  // Fires before `identify` writes the first search to the
                  // person, so it carries its own copy (PostHog only; this
                  // event no longer goes to Customer.io).
                  ...recallFirstSearch(user.uid),
                  source: authSource,
                  method: user.providerData?.[0]?.providerId ?? null,
                  ...(optinPlacement ? { optinPlacement } : {}),
                },
                // Also ahead of `identify`, so PostHog's person has the name
                // from the first event.
                { personSet: personNameProps(user.displayName) }
              )
            }

            // DUP-1903: stamp the new-account moment so a marketing-consent
            // choice captured on the sign-up screen (`account_creation` arm)
            // gets applied by `useFlushMarketingConsent`. Gated here on
            // `existing === false` so a returning sign-in never re-subscribes.
            if (usePendingMarketingConsentStore.getState().consent) {
              usePendingMarketingConsentStore
                .getState()
                .markRegistered(Date.now())
            }

            // Podscribe (signup) — new users only; needs the registered-user id
            // as the device id.
            if (registeredUser?.id && window?.podscribe) {
              window?.podscribe('signup', {
                device_id: registeredUser.id,
                hashed_email: user?.email ? stringToHash(user.email) : null,
                promo: promoCode,
              })
            }
          }
        }
      } else {
        // clear cookie
        deleteCookie(DUPE_AUTH_USER_ID_KEY, {
          path: '/',
          domain:
            process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
              ? '.dupe.com'
              : undefined,
        })

        // Reset dupeInputStore when user signs out
        resetDupeInputStore()
        resetOnboarding()
        // DUP-1903: and the pre-auth marketing-consent capture. It has no owner
        // (the choice predates the account), so anything left here can be picked
        // up by the NEXT person to register in this tab — signing out is exactly
        // when a shared device changes hands.
        usePendingMarketingConsentStore.getState().clear()
      }
    })

    return () => unsubscribe()
  }, [
    maybeRegisterUser,
    resetDupeInputStore,
    loadingAuthUser,
    loadingUserData,
    userData?.id,
    userData?.walletData?.address,
    resetOnboarding,
    authSource,
  ])

  // Identity properties sent to PostHog / Datadog / Intercom.
  //
  // Deliberately a STABLE SUBSET of userData rather than `{...userData}`.
  // posthog-js treats an identify with changed person properties as a reason to
  // refetch flags (`setPersonProperties` → `setPersonPropertiesForFlags(props,
  // /* reloadFeatureFlags */ true)`), and PostHog bills `/flags` per request.
  // Spreading all of userData meant per-search churn — `resultCounts` mutates on
  // every search — refired identify and bought another billed flag request each
  // time. Fields that change with activity (`resultCounts`, `metadata`,
  // `onboarding_data`, `countryCodeUpdatedAt`) are excluded; `email` MUST stay,
  // as the "Internal Users" cohort and the internal flag gates match on it.
  // See docs/references/posthog-flag-audit-2026-07.md.
  const identifyProperties = useMemo(
    () =>
      userData
        ? {
            id: userData.id,
            email: userData.email,
            // The column holds the whole display name; split for greetings.
            ...personNameProps(userData.firstName),
            phone: userData.phone,
            newsletter: userData.newsletter,
            createdAt: userData.createdAt,
            tempUserIds: userData.tempUserIds,
            invitedById: userData.invitedById,
            inviteCode: userData.inviteCode,
            countryCode: userData.countryCode,
            walletAddress: userData.walletData?.address,
          }
        : null,
    [
      userData?.id,
      userData?.email,
      userData?.firstName,
      userData?.phone,
      userData?.newsletter,
      userData?.createdAt,
      // array identity is unstable across refetches — compare by value
      userData?.tempUserIds?.join(','),
      userData?.invitedById,
      userData?.inviteCode,
      userData?.countryCode,
      userData?.walletData?.address,
    ]
  )

  // call analytics.identify with the stable identity subset
  useEffect(() => {
    if (!identifyProperties) return
    if (!authUser) return

    analytics.identify(identifyProperties.id, identifyProperties)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, identifyProperties])

  // Check URL parameters for onboarding
  useEffect(() => {
    if (!authUser) return
    if (isOpen) return

    const url = new URL(window.location.href)
    const hasOnboarding = url.searchParams.get('onboarding') === '1'
    const status = url.searchParams.get('status')

    let source = `onboarding`

    if (status) {
      source += `:${status}`
    }

    if (hasOnboarding) {
      openAuthModal({
        source,
        allowLoggedIn: true,
      })
    }
  }, [authUser, isOpen, openAuthModal])

  // Update onboarding store with user metadata when both authUser and userData are available
  useEffect(() => {
    if (!authUser || !userData) return
    if (loadingAuthUser || loadingUserData) return

    // Extract subscription data from user metadata
    const subscription = userData.metadata?.subscription || {}
    const stripeCustomerId = userData.metadata?.stripeCustomerId
    const stripeSubscriptionId = userData.metadata?.stripeSubscriptionId
    const onboardingData = userData.metadata?.onboarding_data || {}

    // Update onboarding store with user metadata. Seed the department default
    // so a partially onboarded returning shopper starts from a sensible value;
    // saved answers spread last and win. `shoppingFor` is NOT seeded — it's set
    // only when a step collects it, so we never persist an unasked default.
    setOnboardingStepData({
      subscription,
      stripeCustomerId,
      stripeSubscriptionId,
      onboarding_data: {
        department: [...FASHION_DEPARTMENT_DEFAULT],
        ...onboardingData,
      },
    })
  }, [
    authUser,
    userData,
    loadingAuthUser,
    loadingUserData,
    setOnboardingStepData,
  ])

  return (
    <AuthContext.Provider
      value={{
        isOpen,
        openAuthModal,
        closeAuthModal,
        handleAuthSuccess,
        authSource,
        modalTitle,
        modalSubtitle,
        modalIcon,
        modalFooter,
        modalAsset,
        userData,
        loadingUserData,
        refetchUserData,
        extensionInstalled,
        skipPhoneLink,
        disableClose,
        fullScreen,
        blurOverlay,
        skipOnboarding,
        onboardingActive,
        setOnboardingActive,
        onboardingCloseable,
        setOnboardingCloseable,
        updateUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
